import type { ComponentProps } from 'react'
import { useEffect, useImperativeHandle, useRef } from 'react'
import * as React from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import _Button from './BaseButton'
import { useElementWidth } from 'hooks/useElementSize'
import { LargeButtonTypography } from '../Typography/ButtonTypography'

const StyledBaseButton = styled(_Button)`
  --button-border-radius: ${props => props.theme.borderRadius.round};
  --button-size: ${rem(48)};
  --button-icon-size: ${rem(24)};
  --button-padding: ${rem(16)};
  --fab-transition-duration: 0.3s;

  box-shadow: ${props => props.theme.shadow.flat.large};
  justify-content: start;
  overflow: hidden;
  transition: color 0.2s, background-color 0.2s, border-color 0.2s, opacity 0.2s, padding var(--fab-transition-duration), width var(--fab-transition-duration);
  width: calc(var(--button-padding) + var(--button-size) + var(--button-content-width, auto) + ${rem(2)});

  ${LargeButtonTypography}

  > svg:first-of-type {
    flex-shrink: 0;
    height: var(--button-icon-size, auto);
    width: var(--button-icon-size, auto);
    transition: margin 0.2s;
  }

  &:not(.is-collapsed).has-icon-start {
    > svg:first-child,
    > img:first-child {
      margin-right: ${rem(8)};
    }
  }

  > .content {
    opacity: 1;
    transition: opacity var(--fab-transition-duration);
  }

  &.is-collapsed {
    --button-padding: ${rem(11)};

    width: calc(var(--button-size));

    > .content {
      opacity: 0;
    }
  }

  &.kind-primary-experiment.variant-default {
    --color-primary-bg: ${props => props.theme.palette.highlight.primary.normalBackground};
    --color-primary-fg: ${props => props.theme.palette.highlight.primary.normalContrast};
    --color-primary-hover: ${props => props.theme.palette.highlight.primary.normalHover};
    --color-primary-active: ${props => props.theme.palette.highlight.primary.normalActive};
  }
`

interface Props extends Omit<ComponentProps<typeof _Button>, 'kind' | 'isSelected'> {
  /**
   * Whether or not the button is an a collapsed or expanded state
   * In a collapsed state, the component will only be the icon specified
   * In an expanded state, the component will be the icon with text
   **/
  isCollapsed?: boolean
  /** An icon element that will go at the start (often the 'left' side) of the button */
  startIcon: React.ReactElement
  /**
   * @default primary
   */
  kind?: 'primary' | 'primary-experiment'
}

const FloatingActionButton = React.forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    children,
    className,
    isCollapsed,
    nonInteractable,
    startIcon,
    kind = 'primary',
    ...rest
  } = props

  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const contentRef = useRef<HTMLElement | null>(null)
  // credits for the content element width handling goes to @eddiemcleanlux
  const contentWidth = useElementWidth(contentRef)

  useEffect(() => {
    buttonRef.current?.style.setProperty('--button-content-width', `${contentWidth}px`)
  }, [contentWidth])

  useImperativeHandle(ref, () => buttonRef.current!)

  return <StyledBaseButton
    {...rest}
    ref={buttonRef}
    kind="primary"
    className={clsx(
      className,
      `kind-${kind}`,
      {
        'has-icon-start': !!startIcon,
        'is-collapsed': isCollapsed,
      },
    )}
  >
    {startIcon}
    <span className="content" ref={contentRef}>{children}</span>
  </StyledBaseButton>
})

FloatingActionButton.displayName = 'FloatingActionButton'

export default FloatingActionButton
